import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IAlarm, IPaginator, IResponse } from '../core.interface'; // Define tus interfaces aquí

@Injectable({
  providedIn: 'root',
})
export class AlarmService {
  private http = inject(HttpClient);

  private _alarms = signal<IAlarm[]>([]);
  private _historicAlarms = signal<IAlarm[]>([]);
  private _isLoading = signal<boolean>(false);

  private _hasMoreMain = signal<boolean>(true);
  private _hasMoreHistoric = signal<boolean>(true);
  private _alarmsTotalElements = signal<number>(0);
  private _historicTotalElements = signal<number>(0);
  private _pageMain = 0;
  private _pageHistoric = 0;
  readonly alarmsPageSize = 10;
  readonly historicPageSize = this.alarmsPageSize;
  private readonly _size = this.alarmsPageSize;

  private _currentTenantId: string | null = null;
  private _currentFromDate: string | null = null;
  private _currentToDate: string | null = null;
  private _currentHistoricTypeId: number | null = null;
  private _alarmsPageCache = new Map<number, IAlarm[]>();
  private _historicPageCache = new Map<number, IAlarm[]>();

  public alarms = this._alarms.asReadonly();
  public historicAlarms = this._historicAlarms.asReadonly();
  public isLoading = this._isLoading.asReadonly();
  public hasMore = this._hasMoreMain.asReadonly();
  public hasMoreHistoric = this._hasMoreHistoric.asReadonly();
  public alarmsTotalElements = this._alarmsTotalElements.asReadonly();
  public historicTotalElements = this._historicTotalElements.asReadonly();

  /**
   * Obtiene las alarmas agrupadas por tipo
   */
  async fetchAlarms(
    params: {
      tenantId?: string;
      fromDate?: string;
      toDate?: string;
      page?: number;
      force?: boolean;
    } = {},
  ): Promise<void> {
    const { tenantId, fromDate, toDate, page = 0, force = false } = params;

    const tenantChanged = tenantId !== undefined && tenantId !== this._currentTenantId;

    if (tenantId !== undefined) this._currentTenantId = tenantId;
    if (fromDate !== undefined) this._currentFromDate = fromDate;
    if (toDate !== undefined) this._currentToDate = toDate;

    if (tenantChanged) {
      this.clearMainCache();
    }

    if (!this._currentTenantId) return;

    if (!force && this._alarmsPageCache.has(page)) {
      this._pageMain = page;
      this._alarms.set(this._alarmsPageCache.get(page)!);
      return;
    }

    if (force) {
      this._alarmsPageCache.clear();
    }

    if (this._isLoading()) return;

    this._isLoading.set(true);
    this._pageMain = page;

    try {
      let httpParams = new HttpParams()
        .set('groupedByType', 'false')
        .set('page', page.toString())
        .set('size', this._size.toString());

      if (this._currentFromDate)
        httpParams = httpParams.set('from', `${this._currentFromDate}T00:00Z`);
      if (this._currentToDate) httpParams = httpParams.set('to', `${this._currentToDate}T00:00Z`);

      const response = await firstValueFrom(
        this.http.get<IResponse<IPaginator<IAlarm[]>>>(
          `${environment.apiUrl}/tenant/${this._currentTenantId}/meters/alarms`,
          { params: httpParams },
        ),
      );

      const fetched = response.data.content || [];

      this._alarmsPageCache.set(page, fetched);
      this._alarms.set(fetched);
      this._alarmsTotalElements.set(response.data.totalElements);
      this._hasMoreMain.set(!response.data.last);
    } catch (e) {
      console.error('Error fetching alarms:', e);
    } finally {
      this._isLoading.set(false);
    }
  }

  private clearMainCache(): void {
    this._alarmsPageCache.clear();
    this._alarms.set([]);
    this._alarmsTotalElements.set(0);
    this._pageMain = 0;
    this._hasMoreMain.set(true);
  }

  /**
   * Obtiene el histórico de una alarma específica (Vista Detalle)
   */
  async fetchAlarmByType(typeId: number, page = 0, force = false): Promise<void> {
    if (!this._currentTenantId) return;

    if (this._currentHistoricTypeId !== typeId) {
      this.clearHistoricCache();
      this._currentHistoricTypeId = typeId;
    }

    if (!force && this._historicPageCache.has(page)) {
      this._pageHistoric = page;
      this._historicAlarms.set(this._historicPageCache.get(page)!);
      return;
    }

    if (force) {
      this._historicPageCache.clear();
    }

    if (this._isLoading()) return;

    this._isLoading.set(true);
    this._pageHistoric = page;

    try {
      let httpParams = new HttpParams()
        .set('alarmTypeId', typeId.toString())
        .set('page', page.toString())
        .set('size', this._size.toString());

      if (this._currentFromDate)
        httpParams = httpParams.set('from', `${this._currentFromDate}T00:00Z`);
      if (this._currentToDate) httpParams = httpParams.set('to', `${this._currentToDate}T00:00Z`);

      const response = await firstValueFrom(
        this.http.get<IResponse<IPaginator<IAlarm[]>>>(
          `${environment.apiUrl}/tenant/${this._currentTenantId}/meters/alarms`,
          { params: httpParams },
        ),
      );

      const data = response.data;
      const fetched = data.content || [];

      this._historicPageCache.set(page, fetched);
      this._historicAlarms.set(fetched);
      this._historicTotalElements.set(data.totalElements);
      this._hasMoreHistoric.set(!data.last);
    } catch (e) {
      console.error('Error fetching alarm by type:', e);
    } finally {
      this._isLoading.set(false);
    }
  }

  private clearHistoricCache(): void {
    this._historicPageCache.clear();
    this._currentHistoricTypeId = null;
    this._historicAlarms.set([]);
    this._historicTotalElements.set(0);
    this._pageHistoric = 0;
    this._hasMoreHistoric.set(true);
  }

  /**
   * Desactiva una alarma
   */
  async deactivateAlarm(meterId: string, alarmTypeId: number): Promise<boolean> {
    if (!this._currentTenantId) return false;

    try {
      const response = await firstValueFrom(
        this.http.patch(
          `${environment.apiUrl}/tenant/${this._currentTenantId}/meters/${meterId}/alarms/deactivate`,
          { alarmTypeId },
          { observe: 'response' },
        ),
      );
      return response.status === 200 || response.status === 204;
    } catch (e) {
      console.error('Error deactivating alarm:', e);
      return false;
    }
  }

  /**
   * Limpia el estado
   */
  clearAlarms(): void {
    this.clearMainCache();
    this.clearHistoricCache();
    this._isLoading.set(false);
  }
}
